import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import PermissionGate from '../components/PermissionGate';
import Layout from '../components/Layout';
import UserAvatar from '../components/UserAvatar';
import apiService from '../services/apiService';
import toast from 'react-hot-toast';
import {
  Plus,
  Edit,
  Trash2,
  Shield,
  UserPlus,
  Mail,
  Phone,
  Calendar,
  Search,
  Check,
  X,
  User,
  Info,
  Settings,
  Eye,
  EyeOff,
  Power,
  MoreVertical,
  ChevronDown
} from 'lucide-react';

const UserManagement = () => {
  const { user } = useAuth();
  const { hasPermission, isAdmin } = usePermissions();
  const navigate = useNavigate();

  // Date formatting helper function
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // UI State
  const [loading, setLoading] = useState(false);

  // User Management State
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Delete Confirmation Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Dropdown menu state
  const [openDropdown, setOpenDropdown] = useState(null);
  
  // View Profile Modal State
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  
  // Verification Confirmation Modal State
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [userToVerify, setUserToVerify] = useState(null);
  const [verifyReason, setVerifyReason] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  
  // Unverify Confirmation Modal State
  const [showUnverifyModal, setShowUnverifyModal] = useState(false);
  const [userToUnverify, setUserToUnverify] = useState(null);
  const [unverifyReason, setUnverifyReason] = useState('');
  const [unverifyLoading, setUnverifyLoading] = useState(false);
  
  // OTP Verification Modal State
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [userToVerifyOTP, setUserToVerifyOTP] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  
  // Status Confirmation Modal State
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [userToStatusChange, setUserToStatusChange] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    username: '',
    phone: '',
    password: '',
    roles: [],
    status: 'inactive', // Default to inactive for security
    email_verified_at: null // Default to unverified
  });

  // Validation state
  const [fieldValidation, setFieldValidation] = useState({
    email: { isValid: null, message: '' },
    username: { isValid: null, message: '' },
    phone: { isValid: null, message: '' },
    password: { isValid: null, message: '' }
  });
  const [isValidatingEmail, setIsValidatingEmail] = useState(false);
  const [isValidatingUsername, setIsValidatingUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);


  // Load users and roles on component mount
  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  // Cleanup toasts when component unmounts or page changes
  useEffect(() => {
    return () => {
      // Dismiss all toasts when leaving the page
      toast.dismiss();
    };
  }, []);

  // Reset role filter if user doesn't have permission to see super_admin
  useEffect(() => {
    if (selectedRole === 'super_admin' && !hasPermission('users.super_admin')) {
      setSelectedRole('all');
    }
  }, [selectedRole, hasPermission]);

  // Refetch users when search, role filter, or page changes
  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm, selectedRole]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.relative')) {
        setOpenDropdown(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        search: searchTerm,
        role: selectedRole
      });
      
      const response = await apiService.request(`/users?${params}`);
      
      if (response.success) {
        // Filter out super_admin users if user doesn't have permission (safety measure)
        let filteredUsers = response.data;
        if (!hasPermission('users.super_admin')) {
          filteredUsers = response.data.filter(user => 
            !user.roles || !user.roles.some(role => role.slug === 'super_admin')
          );
        }
        setUsers(filteredUsers);
        setTotalPages(response.last_page);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users', {
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await apiService.request('/users/roles/list');
      
      if (response.success) {
        // Filter out super_admin role if user doesn't have permission
        let filteredRoles = response.data;
        if (!hasPermission('users.super_admin')) {
          filteredRoles = response.data.filter(role => role.slug !== 'super_admin');
        }
        setRoles(filteredRoles);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Failed to fetch roles', {
        duration: 3000,
      });
    }
  };

  // Validation functions
  const validateUserEmail = async (email) => {
    if (!email) {
      setFieldValidation(prev => ({
        ...prev,
        email: { isValid: null, message: '' }
      }));
      return;
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setFieldValidation(prev => ({
        ...prev,
        email: { isValid: false, message: 'Please enter a valid email address' }
      }));
      return;
    }

    setIsValidatingEmail(true);
    try {
      const excludeId = editingUser ? editingUser.id : null;
      const response = await apiService.validateUserEmail(email, excludeId);
      
      setFieldValidation(prev => ({
        ...prev,
        email: {
          isValid: response.available,
          message: response.message
        }
      }));
    } catch (error) {
      console.error('Error validating user email:', error);
      setFieldValidation(prev => ({
        ...prev,
        email: { isValid: false, message: 'Error validating email address' }
      }));
    } finally {
      setIsValidatingEmail(false);
    }
  };

  const validateUserUsername = async (username) => {
    if (!username) {
      setFieldValidation(prev => ({
        ...prev,
        username: { isValid: null, message: '' }
      }));
      return;
    }

    // Basic username format validation
    if (username.length < 3) {
      setFieldValidation(prev => ({
        ...prev,
        username: { isValid: false, message: 'Username must be at least 3 characters long' }
      }));
      return;
    }

    if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
      setFieldValidation(prev => ({
        ...prev,
        username: { isValid: false, message: 'Username can only contain letters, numbers, underscores, and hyphens' }
      }));
      return;
    }

    setIsValidatingUsername(true);
    try {
      const excludeId = editingUser ? editingUser.id : null;
      const response = await apiService.validateUserUsername(username, excludeId);
      
      setFieldValidation(prev => ({
        ...prev,
        username: {
          isValid: response.available,
          message: response.message
        }
      }));
    } catch (error) {
      console.error('Error validating username:', error);
      setFieldValidation(prev => ({
        ...prev,
        username: { isValid: false, message: 'Error validating username' }
      }));
    } finally {
      setIsValidatingUsername(false);
    }
  };

  const validatePhoneNumber = (phone) => {
    if (!phone) {
      setFieldValidation(prev => ({
        ...prev,
        phone: { isValid: null, message: '' }
      }));
      return;
    }

    // Remove any non-digit characters for validation
    const digitsOnly = phone.replace(/\D/g, '');
    
    if (digitsOnly.length !== 10) {
      setFieldValidation(prev => ({
        ...prev,
        phone: { isValid: false, message: 'Phone number must be exactly 10 digits' }
      }));
      return;
    }

    setFieldValidation(prev => ({
      ...prev,
      phone: { isValid: true, message: 'Phone number is valid' }
    }));
  };

  const validatePassword = (password) => {
    if (!password) {
      setFieldValidation(prev => ({
        ...prev,
        password: { isValid: null, message: '' }
      }));
      return;
    }

    if (password.length < 8) {
      setFieldValidation(prev => ({
        ...prev,
        password: { isValid: false, message: 'Password must be at least 8 characters long' }
      }));
      return;
    }

    setFieldValidation(prev => ({
      ...prev,
      password: { isValid: true, message: 'Password is valid' }
    }));
  };

  // Debounced validation calls
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  const debouncedValidateEmail = debounce((email) => {
    validateUserEmail(email);
  }, 500);

  const debouncedValidateUsername = debounce((username) => {
    validateUserUsername(username);
  }, 500);

  const handleAddUser = async () => {
    // Validate all fields before submission
    const isFormValid = (
      fieldValidation.email.isValid === true &&
      fieldValidation.username.isValid === true &&
      fieldValidation.phone.isValid === true &&
      fieldValidation.password.isValid === true &&
      newUser.name.trim() !== '' &&
      newUser.roles.length > 0
    );

    if (!isFormValid) {
      toast.error('Please fix all validation errors before submitting', {
        duration: 3000,
      });
      return;
    }

    try {
      // Remove email_verified_at from the request data as it's not expected by backend
      const userData = {
        name: newUser.name,
        email: newUser.email,
        username: newUser.username,
        phone: newUser.phone || null, // Convert empty string to null for nullable field
        password: newUser.password,
        roles: newUser.roles,
        status: newUser.status
      };
      
      const response = await apiService.request('/users', {
        method: 'POST',
        body: JSON.stringify(userData)
      });
      
      if (response.success) {
        toast.success('User added successfully! OTP sent to email for verification.', {
          duration: 4000,
          icon: '📧',
          style: {
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: '#fff',
          },
        });
        setShowAddUserModal(false);
        setNewUser({ name: '', email: '', username: '', phone: '', password: '', roles: [], status: 'inactive', email_verified_at: null });
        setFieldValidation({
          email: { isValid: null, message: '' },
          username: { isValid: null, message: '' },
          phone: { isValid: null, message: '' },
          password: { isValid: null, message: '' }
        });
        setShowPassword(false);
        fetchUsers();
      }
    } catch (error) {
      console.error('Error adding user:', error);
      
      // Try to extract validation errors from the error response
      let errorMessage = 'Failed to add user';
      if (error.details) {
        // Show specific validation errors
        const errorMessages = Object.values(error.details).flat();
        errorMessage = errorMessages.join(', ');
      } else if (error.message && error.message.includes('Validation failed')) {
        errorMessage = 'Please check all fields and try again';
      }
      
      toast.error(errorMessage, {
        duration: 5000,
      });
    }
  };

  const handleEditUser = (user) => {
    // Set editing user directly without checking current page
    // The user exists if we can click on it
    setEditingUser({
      id: user.id,
      name: user.name,
      email: user.email,
      username: user.username,
      phone: user.phone,
      password: '', // Don't pre-fill password
      roles: user.roles.map(role => role.id)
    });
    setShowAddUserModal(true);
  };

  const handleUpdateUser = async () => {
    try {
      const response = await apiService.request(`/users/${editingUser.id}`, {
        method: 'PUT',
        body: JSON.stringify(editingUser)
      });
      
      if (response.success) {
        toast.success('User updated successfully!', {
          duration: 3000,
        });
        setShowAddUserModal(false);
        setEditingUser(null);
        fetchUsers();
      } else {
        throw new Error(response.message || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      
      // Check if it's a 404 error (user not found)
      if (error.message.includes('404') || error.message.includes('not found')) {
        toast.error('User not found. The user may have been deleted.', {
          duration: 3000,
        });
        setShowAddUserModal(false);
        setEditingUser(null);
        fetchUsers(); // Refresh the user list
      } else {
        toast.error(`Failed to update user: ${error.message}`, {
          duration: 3000,
        });
      }
    }
  };

  const handleDeleteUser = (userId) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setUserToDelete(user);
      setShowDeleteModal(true);
    }
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeleteLoading(true);
    try {
      const response = await apiService.request(`/users/${userToDelete.id}`, {
          method: 'DELETE'
        });
        
        if (response.success) {
        toast.success('User deleted successfully!', {
          duration: 3000,
          icon: '🗑️',
          style: {
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: '#fff',
          },
        });
          // Close edit modal if the deleted user was being edited
        if (editingUser && editingUser.id === userToDelete.id) {
            setShowAddUserModal(false);
            setEditingUser(null);
          }
          fetchUsers();
        setShowDeleteModal(false);
        setUserToDelete(null);
        } else {
          throw new Error(response.message || 'Failed to delete user');
        }
      } catch (error) {
        console.error('Error deleting user:', error);
      toast.error(`Failed to delete user: ${error.message}`, {
        duration: 4000,
        icon: '❌',
        style: {
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: '#fff',
        },
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDeleteUser = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleViewUser = (user) => {
    setViewingUser(user);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewingUser(null);
  };

  const handleVerifyUser = (user) => {
    setUserToVerify(user);
    setVerifyReason('');
    setShowVerifyModal(true);
  };

  const handleVerifyUserClick = (user) => {
    // Close status modal if open
    setShowStatusModal(false);
    setUserToStatusChange(null);
    setStatusReason('');
    // Open OTP verification modal instead of manual verification
    handleVerifyWithOTP(user);
  };

  const handleUnverifyUser = (user) => {
    setUserToUnverify(user);
    setUnverifyReason('');
    setShowUnverifyModal(true);
  };

  const confirmVerifyUser = async () => {
    if (!userToVerify || !verifyReason.trim()) {
      toast.error('Please provide a reason for manual verification', {
        duration: 3000,
        icon: '⚠️',
        style: {
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          color: '#fff',
        },
      });
      return;
    }

    setVerifyLoading(true);
    try {
      const response = await apiService.request(`/users/${userToVerify.id}/verify`, {
        method: 'POST',
        body: JSON.stringify({ 
          reason: verifyReason.trim(),
          admin_action: true,
          admin_id: user?.id,
          admin_name: user?.name || user?.username
        })
      });
      
      if (response.success) {
        toast.success('User verified successfully!', {
          duration: 3000,
          icon: '✅',
          style: {
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: '#fff',
          },
        });
        fetchUsers();
        setShowVerifyModal(false);
        setUserToVerify(null);
        setVerifyReason('');
      } else {
        throw new Error(response.message || 'Failed to verify user');
      }
    } catch (error) {
      console.error('Error verifying user:', error);
      toast.error(`Failed to verify user: ${error.message}`, {
        duration: 4000,
        icon: '❌',
        style: {
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: '#fff',
        },
      });
    } finally {
      setVerifyLoading(false);
    }
  };

  const cancelVerifyUser = () => {
    setShowVerifyModal(false);
    setUserToVerify(null);
    setVerifyReason('');
  };

  const confirmUnverifyUser = async () => {
    if (!userToUnverify || !unverifyReason.trim()) {
      toast.error('Please provide a reason for removing verification', {
        duration: 3000,
        icon: '⚠️',
        style: {
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          color: '#fff',
        },
      });
      return;
    }

    setUnverifyLoading(true);
    try {
      const response = await apiService.request(`/users/${userToUnverify.id}/unverify`, {
        method: 'POST',
        body: JSON.stringify({ 
          reason: unverifyReason.trim(),
          admin_action: true,
          admin_id: user?.id,
          admin_name: user?.name
        })
      });
      
      if (response.success) {
        toast.success('Email verification removed successfully!', {
          duration: 3000,
          icon: '⚠️',
          style: {
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            color: '#fff',
          },
        });
        fetchUsers();
        setShowUnverifyModal(false);
        setUserToUnverify(null);
        setUnverifyReason('');
      } else {
        throw new Error(response.message || 'Failed to remove verification');
      }
    } catch (error) {
      console.error('Error unverifying user:', error);
      toast.error(`Failed to remove verification: ${error.message}`, {
        duration: 4000,
        icon: '❌',
        style: {
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: '#fff',
        },
      });
    } finally {
      setUnverifyLoading(false);
    }
  };

  const cancelUnverifyUser = () => {
    setShowUnverifyModal(false);
    setUserToUnverify(null);
    setUnverifyReason('');
  };

  const handleVerifyWithOTP = (user) => {
    setUserToVerifyOTP(user);
    setOtpCode('');
    setOtpError('');
    setShowOTPModal(true);
  };

  const handleVerifyOTP = async () => {
    if (!userToVerifyOTP || !otpCode.trim()) {
      setOtpError('Please enter the OTP code');
      return;
    }

    if (otpCode.length !== 6) {
      setOtpError('OTP code must be 6 digits');
      return;
    }

    setOtpLoading(true);
    setOtpError('');

    try {
      const response = await apiService.request(`/users/${userToVerifyOTP.id}/verify-otp`, {
        method: 'POST',
        body: JSON.stringify({ otp: otpCode.trim() })
      });

      if (response.success) {
        toast.success('OTP verified successfully! User email is now verified.', {
          duration: 3000,
          icon: '✅',
          style: {
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: '#fff',
          },
        });
        fetchUsers();
        setShowOTPModal(false);
        setUserToVerifyOTP(null);
        setOtpCode('');
      } else {
        setOtpError(response.message || 'Invalid OTP code');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setOtpError(error.message || 'Failed to verify OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!userToVerifyOTP) return;

    setOtpLoading(true);
    setOtpError('');

    try {
      const response = await apiService.request(`/users/${userToVerifyOTP.id}/resend-otp`, {
        method: 'POST'
      });

      if (response.success) {
        toast.success('OTP resent successfully!', {
          duration: 3000,
          icon: '📧',
          style: {
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: '#fff',
          },
        });
        setOtpCode('');
        setOtpError('');
      } else {
        setOtpError(response.message || 'Failed to resend OTP');
      }
    } catch (error) {
      console.error('Error resending OTP:', error);
      setOtpError(error.message || 'Failed to resend OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const cancelOTPVerification = () => {
    setShowOTPModal(false);
    setUserToVerifyOTP(null);
    setOtpCode('');
    setOtpError('');
  };

  const handleToggleUserStatus = (user) => {
    const currentStatus = user.status || 'active';
    const targetStatus = currentStatus === 'active' ? 'inactive' : 'active';
    
    setUserToStatusChange(user);
    setNewStatus(targetStatus);
    setStatusReason('');
    setShowStatusModal(true);
  };

  const confirmStatusChange = async () => {
    if (!userToStatusChange || !statusReason.trim()) {
      toast.error('Please provide a reason for this status change', {
        duration: 3000,
        icon: '⚠️',
        style: {
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          color: '#fff',
        },
      });
      return;
    }

    setStatusLoading(true);
    try {
      const response = await apiService.request(`/users/${userToStatusChange.id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ 
          status: newStatus,
          reason: statusReason.trim(),
          admin_action: true,
          admin_id: user?.id,
          admin_name: user?.name || user?.username
        })
      });
      
      if (response.success) {
        const actionText = newStatus === 'active' ? 'activated' : 'deactivated';
        toast.success(`User ${actionText} successfully!`, {
          duration: 3000,
          icon: newStatus === 'active' ? '✅' : '⏸️',
          style: {
            background: newStatus === 'active' 
              ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
              : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
            color: '#fff',
          },
        });
        fetchUsers();
        setShowStatusModal(false);
        setUserToStatusChange(null);
        setNewStatus('');
        setStatusReason('');
      } else {
        throw new Error(response.message || `Failed to ${newStatus === 'active' ? 'activate' : 'deactivate'} user`);
      }
    } catch (error) {
      console.error(`Error ${newStatus === 'active' ? 'activating' : 'deactivating'} user:`, error);
      
      // Check if error is due to unverified email (trying to activate)
      if (newStatus === 'active' && error.message && error.message.toLowerCase().includes('verify')) {
        // Special toast for unverified users - more helpful message
        toast.error((t) => (
          <div className="flex flex-col space-y-2">
            <div className="flex items-start space-x-2">
              <span className="text-xl">⚠️</span>
              <div className="flex-1">
                <p className="font-semibold">Cannot Activate Unverified User</p>
                <p className="text-sm mt-1">
                  {userToStatusChange?.name || 'This user'} must verify their email address before activation.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-white/20">
              <button
                onClick={() => {
                  toast.dismiss(t.id);
                  handleVerifyUserClick(userToStatusChange);
                }}
                className="text-sm px-3 py-1 bg-white/20 hover:bg-white/30 rounded transition-colors"
              >
                📧 Verify User Now
              </button>
              <button
                onClick={() => toast.dismiss(t.id)}
                className="text-sm px-3 py-1 hover:bg-white/10 rounded transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        ), {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            color: '#fff',
            maxWidth: '500px',
          },
        });
      } else {
        // Regular error toast for other errors
        toast.error(`Failed to ${newStatus === 'active' ? 'activate' : 'deactivate'} user: ${error.message}`, {
          duration: 4000,
          icon: '❌',
          style: {
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: '#fff',
          },
        });
      }
    } finally {
      setStatusLoading(false);
    }
  };

  const cancelStatusChange = () => {
    setShowStatusModal(false);
    setUserToStatusChange(null);
    setNewStatus('');
    setStatusReason('');
  };

  const getRoleColor = (roleSlug) => {
    switch (roleSlug) {
      case 'super_admin': return 'bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-800 border border-orange-200';
      case 'admin': return 'bg-red-100 text-red-800';
      case 'manager': return 'bg-blue-100 text-blue-800';
      case 'cashier': return 'bg-green-100 text-green-800';
      case 'viewer': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleIcon = (roleSlug) => {
    switch (roleSlug) {
      case 'super_admin': return '⭐';
      case 'admin': return '👑';
      case 'manager': return '💼';
      case 'cashier': return '🛒';
      case 'viewer': return '👁️';
      default: return '👤';
    }
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  return (
    <Layout>
      <div className="space-y-6">
            {/* Page Header */}
            <div className="mb-6 sm:mb-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-16 gap-4">
                <div className="flex-1">
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-indigo-900 bg-clip-text text-transparent">
                    User Management
                  </h1>
                  <p className="text-gray-600 text-sm sm:text-base mt-2">Manage system users and their permissions</p>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-3">
                  <button
                    onClick={fetchUsers}
                    className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-gray-700 hover:to-gray-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm font-medium"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    <span className="hidden xs:inline">Refresh</span>
                  </button>
                  <PermissionGate permission="users.create">
                    <button
                      onClick={() => setShowAddUserModal(true)}
                      className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm font-medium"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add User
                      <span className="hidden xs:inline">Add User</span>
                    </button>
                  </PermissionGate>
                </div>
              </div>
            </div>

            {/* Search and Filter Controls */}
            <div className="bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-blue-100/50 backdrop-blur-sm mb-6">
              <div className="flex flex-col gap-4">
                {/* Search Input */}
                <div className="w-full">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search users by name, email, or phone..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                    />
                  </div>
                </div>
                
                {/* Role Filter */}
                <div className="w-full sm:w-48">
                  <select
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                  >
                    <option value="all">All Roles</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.slug}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Users Table */}
            <div className="bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-blue-100/50 backdrop-blur-sm">
              {/* Mobile Card View - Hidden on larger screens */}
              <div className="block md:hidden space-y-4">
                {users.map((user) => (
                  <div key={user.id} className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                    {/* User Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <UserAvatar user={user} size="lg" />
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{user.name}</p>
                          <p className="text-xs text-gray-500">ID: {user.id}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(user.status || 'active')}`}>
                          {user.status === 'inactive' ? 'Inactive' : 'Active'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Contact Info */}
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center space-x-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-700 truncate">{user.email}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-700">{user.phone}</span>
                      </div>
                    </div>
                    
                    {/* Roles */}
                    <div className="mb-3">
                      <div className="flex flex-wrap gap-1">
                        {user.roles && user.roles.length > 0 ? (
                          user.roles.map((role, index) => (
                            <span
                              key={index}
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(role.slug)}`}
                            >
                              <span className="mr-1">{getRoleIcon(role.slug)}</span>
                              {role.name}
                            </span>
                          ))
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            No Role
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Verification Status */}
                    <div className="mb-3">
                      <div className="text-xs text-gray-500">
                        {user.email_verified_at ? '✓ Verified' : '⚠ Unverified'}
                      </div>
                      <div className="text-xs text-gray-500">
                        Created: {formatDate(user.created_at)}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => handleViewUser(user)}
                          className="group/btn relative p-2 text-blue-600 bg-blue-100 hover:bg-blue-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                          title="View User Profile"
                        >
                          <Eye size={18} />
                          <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                            View
                          </span>
                        </button>
                        <PermissionGate permission="users.edit">
                          <button 
                            onClick={() => handleEditUser(user)}
                            className="group/btn relative p-2 text-green-600 bg-green-100 hover:bg-green-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                            title="Edit User"
                          >
                            <Edit size={18} />
                            <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                              Edit
                            </span>
                          </button>
                        </PermissionGate>
                        
                        {/* More Actions Dropdown */}
                        <div className="relative">
                          <button 
                            onClick={() => setOpenDropdown(openDropdown === `card-${user.id}` ? null : `card-${user.id}`)}
                            className="group/btn relative p-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                            title="More Actions"
                          >
                            <MoreVertical size={18} />
                            <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                              More
                            </span>
                          </button>
                          
                          {/* Dropdown Menu */}
                          {openDropdown === `card-${user.id}` && (
                            <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                              <div className="py-1">
                                {user.email_verified_at ? (
                                  <button 
                                    onClick={() => {
                                      handleUnverifyUser(user);
                                      setOpenDropdown(null);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-orange-600 hover:bg-orange-50 flex items-center space-x-2"
                                  >
                                    <X size={16} />
                                    <span>Remove Verification</span>
                                  </button>
                                ) : (
                                  <button 
                                    onClick={() => {
                                      handleVerifyWithOTP(user);
                                      setOpenDropdown(null);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50 flex items-center space-x-2"
                                  >
                                    <Check size={16} />
                                    <span>Verify User with OTP</span>
                                  </button>
                                )}
                                
                                <button 
                                  onClick={() => {
                                    handleToggleUserStatus(user);
                                    setOpenDropdown(null);
                                  }}
                                  className={`w-full px-4 py-2 text-left text-sm flex items-center space-x-2 ${
                                    user.status === 'inactive' 
                                      ? 'text-green-600 hover:bg-green-50' 
                                      : 'text-orange-600 hover:bg-orange-50'
                                  }`}
                                >
                                  <Power size={16} />
                                  <span>{user.status === 'inactive' ? 'Activate User' : 'Deactivate User'}</span>
                                </button>
                                
                                <PermissionGate permission="users.delete">
                                  <button 
                                    onClick={() => {
                                      handleDeleteUser(user.id);
                                      setOpenDropdown(null);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2 border-t border-gray-100"
                                  >
                                    <Trash2 size={16} />
                                    <span>Delete User</span>
                                  </button>
                                </PermissionGate>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Desktop Table View - Hidden on mobile */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">User</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Contact</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Role</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Created</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                        <td className="py-4 px-4">
                          <div className="flex items-center space-x-3">
                            <UserAvatar user={user} size="md" />
                            <div>
                              <p className="font-medium text-gray-900">{user.name}</p>
                              <p className="text-sm text-gray-500">ID: {user.id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="space-y-1">
                            <div className="flex items-center space-x-2">
                              <Mail className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-700">{user.email}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Phone className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-700">{user.phone}</span>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex flex-wrap gap-1">
                            {user.roles && user.roles.length > 0 ? (
                              user.roles.map((role, index) => (
                                <span
                                  key={index}
                                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(role.slug)}`}
                                  title={role.description}
                                >
                                  <span className="mr-1">{getRoleIcon(role.slug)}</span>
                                  {role.name}
                                </span>
                              ))
                            ) : (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                No Role
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="space-y-1">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(user.status || 'active')}`}>
                              {user.status === 'inactive' ? 'Inactive' : 'Active'}
                          </span>
                            <div className="text-xs text-gray-500">
                              {user.email_verified_at ? '✓ Verified' : '⚠ Unverified'}
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-700">{formatDate(user.created_at)}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center space-x-2">
                            <button 
                              onClick={() => handleViewUser(user)}
                              className="group/btn relative p-2 text-blue-600 bg-blue-100 hover:bg-blue-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                              title="View User Profile"
                            >
                              <Eye size={18} />
                              <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                View
                              </span>
                            </button>
                            <PermissionGate permission="users.edit">
                              <button 
                                onClick={() => handleEditUser(user)}
                                className="group/btn relative p-2 text-green-600 bg-green-100 hover:bg-green-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                                title="Edit User"
                              >
                                <Edit size={18} />
                                <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                  Edit
                                </span>
                              </button>
                            </PermissionGate>
                            
                            {/* More Actions Dropdown */}
                            <div className="relative">
                              <button 
                                onClick={() => setOpenDropdown(openDropdown === `table-${user.id}` ? null : `table-${user.id}`)}
                                className="group/btn relative p-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                                title="More Actions"
                              >
                                <MoreVertical size={18} />
                                <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                  More
                                </span>
                              </button>
                              
                              {/* Dropdown Menu */}
                              {openDropdown === `table-${user.id}` && (
                                <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                                  <div className="py-1">
                                    {user.email_verified_at ? (
                                      <button 
                                        onClick={() => {
                                          handleUnverifyUser(user);
                                          setOpenDropdown(null);
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm text-orange-600 hover:bg-orange-50 flex items-center space-x-2"
                                      >
                                        <X size={16} />
                                        <span>Remove Verification</span>
                                      </button>
                                    ) : (
                                      <button 
                                        onClick={() => {
                                          handleVerifyWithOTP(user);
                                          setOpenDropdown(null);
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50 flex items-center space-x-2"
                                      >
                                        <Check size={16} />
                                        <span>Verify User with OTP</span>
                                      </button>
                                    )}
                                    
                                    <button 
                                      onClick={() => {
                                        handleToggleUserStatus(user);
                                        setOpenDropdown(null);
                                      }}
                                      className={`w-full px-4 py-2 text-left text-sm flex items-center space-x-2 ${
                                        user.status === 'inactive' 
                                          ? 'text-green-600 hover:bg-green-50' 
                                          : 'text-orange-600 hover:bg-orange-50'
                                      }`}
                                    >
                                      <Power size={16} />
                                      <span>{user.status === 'inactive' ? 'Activate User' : 'Deactivate User'}</span>
                                    </button>
                                    
                                    <PermissionGate permission="users.delete">
                                      <button 
                                        onClick={() => {
                                          handleDeleteUser(user.id);
                                          setOpenDropdown(null);
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2 border-t border-gray-100"
                                      >
                                        <Trash2 size={16} />
                                        <span>Delete User</span>
                                      </button>
                                    </PermissionGate>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
      </div>

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 px-6 py-4 text-white relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                    {editingUser ? (
                      <Edit className="w-5 h-5 text-white" />
                    ) : (
                      <UserPlus className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">
                      {editingUser ? 'Edit User Profile' : 'Add New User'}
                    </h3>
                    <p className="text-blue-100 text-sm">
                      {editingUser ? 'Update user information and permissions' : 'Create a new user account with roles and permissions'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowAddUserModal(false);
                    setEditingUser(null);
                    setNewUser({ name: '', email: '', username: '', phone: '', password: '', roles: [], status: 'inactive', email_verified_at: null });
                    setFieldValidation({
                      email: { isValid: null, message: '' },
                      username: { isValid: null, message: '' },
                      phone: { isValid: null, message: '' },
                      password: { isValid: null, message: '' }
                    });
                    setShowPassword(false);
                  }}
                  className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-all duration-200 backdrop-blur-sm"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 max-h-[calc(95vh-120px)] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column - Basic Information */}
                <div className="space-y-5">
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                    <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
                      <User className="w-4 h-4 mr-2 text-blue-600" />
                      Basic Information
                    </h4>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                        <div className="relative">
                          <input
                            type="text"
                            value={editingUser ? editingUser.name : newUser.name}
                            onChange={(e) => {
                              if (editingUser) {
                                setEditingUser({...editingUser, name: e.target.value});
                              } else {
                                setNewUser({...newUser, name: e.target.value});
                              }
                            }}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/70 backdrop-blur-sm"
                            placeholder="Enter full name"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                        <div className="relative">
                          <input
                            type="email"
                            value={editingUser ? editingUser.email : newUser.email}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (editingUser) {
                                setEditingUser({...editingUser, email: value});
                              } else {
                                setNewUser({...newUser, email: value});
                              }
                              debouncedValidateEmail(value);
                            }}
                            className={`w-full px-4 py-3 pr-10 border rounded-xl focus:ring-2 focus:border-transparent transition-all duration-200 bg-white/70 backdrop-blur-sm ${
                              fieldValidation.email.isValid === false 
                                ? 'border-red-300 focus:ring-red-500' 
                                : fieldValidation.email.isValid === true 
                                ? 'border-green-300 focus:ring-green-500'
                                : 'border-gray-200 focus:ring-blue-500'
                            }`}
                            placeholder="user@example.com"
                          />
                          {isValidatingEmail && (
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            </div>
                          )}
                          {fieldValidation.email.isValid === true && !isValidatingEmail && (
                            <Check className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-green-500" />
                          )}
                          {fieldValidation.email.isValid === false && !isValidatingEmail && (
                            <X className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
                          )}
                        </div>
                        {fieldValidation.email.message && (
                          <p className={`text-xs mt-1 ${fieldValidation.email.isValid ? 'text-green-600' : 'text-red-600'}`}>
                            {fieldValidation.email.message}
                          </p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                        <div className="relative">
                          <input
                            type="text"
                            value={editingUser ? editingUser.username : newUser.username}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (editingUser) {
                                setEditingUser({...editingUser, username: value});
                              } else {
                                setNewUser({...newUser, username: value});
                              }
                              debouncedValidateUsername(value);
                            }}
                            className={`w-full px-4 py-3 pr-10 border rounded-xl focus:ring-2 focus:border-transparent transition-all duration-200 bg-white/70 backdrop-blur-sm ${
                              fieldValidation.username.isValid === false 
                                ? 'border-red-300 focus:ring-red-500' 
                                : fieldValidation.username.isValid === true 
                                ? 'border-green-300 focus:ring-green-500'
                                : 'border-gray-200 focus:ring-blue-500'
                            }`}
                            placeholder="Enter username"
                          />
                          {isValidatingUsername && (
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            </div>
                          )}
                          {fieldValidation.username.isValid === true && !isValidatingUsername && (
                            <Check className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-green-500" />
                          )}
                          {fieldValidation.username.isValid === false && !isValidatingUsername && (
                            <X className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
                          )}
                        </div>
                        {fieldValidation.username.message && (
                          <p className={`text-xs mt-1 ${fieldValidation.username.isValid ? 'text-green-600' : 'text-red-600'}`}>
                            {fieldValidation.username.message}
                          </p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                        <div className="relative">
                          <input
                            type="tel"
                            value={editingUser ? editingUser.phone : newUser.phone}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (editingUser) {
                                setEditingUser({...editingUser, phone: value});
                              } else {
                                setNewUser({...newUser, phone: value});
                              }
                              validatePhoneNumber(value);
                            }}
                            className={`w-full px-4 py-3 pr-10 border rounded-xl focus:ring-2 focus:border-transparent transition-all duration-200 bg-white/70 backdrop-blur-sm ${
                              fieldValidation.phone.isValid === false 
                                ? 'border-red-300 focus:ring-red-500' 
                                : fieldValidation.phone.isValid === true 
                                ? 'border-green-300 focus:ring-green-500'
                                : 'border-gray-200 focus:ring-blue-500'
                            }`}
                            placeholder="Enter 10-digit phone number"
                            maxLength="10"
                          />
                          {fieldValidation.phone.isValid === true && (
                            <Check className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-green-500" />
                          )}
                          {fieldValidation.phone.isValid === false && (
                            <X className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
                          )}
                        </div>
                        {fieldValidation.phone.message && (
                          <p className={`text-xs mt-1 ${fieldValidation.phone.isValid ? 'text-green-600' : 'text-red-600'}`}>
                            {fieldValidation.phone.message}
                          </p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Password {editingUser && <span className="text-gray-500 text-xs">(leave blank to keep current)</span>}
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            value={editingUser ? editingUser.password : newUser.password}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (editingUser) {
                                setEditingUser({...editingUser, password: value});
                              } else {
                                setNewUser({...newUser, password: value});
                              }
                              validatePassword(value);
                            }}
                            className={`w-full px-4 py-3 pr-20 border rounded-xl focus:ring-2 focus:border-transparent transition-all duration-200 bg-white/70 backdrop-blur-sm ${
                              fieldValidation.password.isValid === false 
                                ? 'border-red-300 focus:ring-red-500' 
                                : fieldValidation.password.isValid === true 
                                ? 'border-green-300 focus:ring-green-500'
                                : 'border-gray-200 focus:ring-blue-500'
                            }`}
                            placeholder={editingUser ? "Enter new password (min 8 characters)" : "Enter password (min 8 characters)"}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-10 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                          {fieldValidation.password.isValid === true && (
                            <Check className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-green-500" />
                          )}
                          {fieldValidation.password.isValid === false && (
                            <X className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
                          )}
                        </div>
                        {fieldValidation.password.message && (
                          <p className={`text-xs mt-1 ${fieldValidation.password.isValid ? 'text-green-600' : 'text-red-600'}`}>
                            {fieldValidation.password.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Roles & Status */}
                <div className="space-y-5">
                  {/* Roles Section */}
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-100">
                    <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
                      <Shield className="w-4 h-4 mr-2 text-purple-600" />
                      Roles & Permissions
                    </h4>
                    <div className="space-y-3 max-h-48 overflow-y-auto">
                      {roles.map((role) => (
                        <label key={role.id} className="flex items-center space-x-3 p-3 bg-white/60 rounded-lg hover:bg-white/80 transition-all duration-200 cursor-pointer border border-transparent hover:border-purple-200">
                          <input
                            type="checkbox"
                            checked={(editingUser ? editingUser.roles : newUser.roles).includes(role.id)}
                            onChange={(e) => {
                              if (editingUser) {
                                if (e.target.checked) {
                                  setEditingUser({...editingUser, roles: [...editingUser.roles, role.id]});
                                } else {
                                  setEditingUser({...editingUser, roles: editingUser.roles.filter(id => id !== role.id)});
                                }
                              } else {
                                if (e.target.checked) {
                                  setNewUser({...newUser, roles: [...newUser.roles, role.id]});
                                } else {
                                  setNewUser({...newUser, roles: newUser.roles.filter(id => id !== role.id)});
                                }
                              }
                            }}
                            className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 focus:ring-offset-0"
                          />
                          <span className="text-sm text-gray-700 flex items-center space-x-2">
                            <span className="text-lg">{getRoleIcon(role.slug)}</span>
                            <span className="font-medium">{role.name}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-3 flex items-center">
                      <Info className="w-3 h-3 mr-1" />
                      Select one or more roles for this user
                    </p>
                  </div>
                  
                  {/* Account Status - Only show for new users */}
                  {!editingUser && (
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100">
                      <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
                        <Settings className="w-4 h-4 mr-2 text-green-600" />
                        Initial Account Status
                      </h4>
                      <div className="space-y-3">
                        <label className="flex items-start space-x-3 p-3 bg-white/60 rounded-lg hover:bg-white/80 transition-all duration-200 cursor-pointer border border-transparent hover:border-gray-200">
                          <input
                            type="radio"
                            name="status"
                            value="inactive"
                            checked={newUser.status === 'inactive'}
                            onChange={(e) => setNewUser({...newUser, status: e.target.value})}
                            className="w-4 h-4 text-gray-600 focus:ring-gray-500 focus:ring-offset-0 mt-0.5"
                          />
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="w-2 h-2 bg-gray-500 rounded-full"></span>
                              <span className="text-sm font-medium text-gray-700">Inactive</span>
                            </div>
                            <p className="text-xs text-gray-600">User cannot log in until activated (Recommended)</p>
                          </div>
                        </label>
                        <label className="flex items-start space-x-3 p-3 bg-white/60 rounded-lg hover:bg-white/80 transition-all duration-200 cursor-pointer border border-transparent hover:border-green-200">
                          <input
                            type="radio"
                            name="status"
                            value="active"
                            checked={newUser.status === 'active'}
                            onChange={(e) => setNewUser({...newUser, status: e.target.value})}
                            className="w-4 h-4 text-green-600 focus:ring-green-500 focus:ring-offset-0 mt-0.5"
                          />
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                              <span className="text-sm font-medium text-gray-700">Active</span>
                            </div>
                            <p className="text-xs text-gray-600">User can log in immediately</p>
                          </div>
                        </label>
                      </div>
                      
                      {/* Security Notice */}
                      <div className="mt-4 p-3 bg-blue-50/80 border border-blue-200/60 rounded-lg backdrop-blur-sm">
                        <div className="flex items-start space-x-2">
                          <Shield className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs text-blue-800 font-medium">Security Notice</p>
                            <p className="text-xs text-blue-700 mt-1">
                              New users will be created as <strong>inactive and unverified</strong> by default. 
                              You can manually verify and activate them after reviewing their details.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-end space-y-3 sm:space-y-0 sm:space-x-3">
              <button
                onClick={() => {
                  setShowAddUserModal(false);
                  setEditingUser(null);
                  setNewUser({ name: '', email: '', username: '', phone: '', password: '', roles: [], status: 'inactive', email_verified_at: null });
                  setFieldValidation({
                    email: { isValid: null, message: '' },
                    username: { isValid: null, message: '' },
                    phone: { isValid: null, message: '' },
                    password: { isValid: null, message: '' }
                  });
                  setShowPassword(false);
                }}
                className="px-6 py-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-all duration-200 font-medium order-2 sm:order-1"
              >
                Cancel
              </button>
              <button
                onClick={editingUser ? handleUpdateUser : handleAddUser}
                disabled={
                  editingUser 
                    ? (!editingUser.name || !editingUser.email || !editingUser.username || editingUser.roles.length === 0)
                    : (!newUser.name || !newUser.email || !newUser.username || !newUser.password || newUser.roles.length === 0)
                }
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none order-1 sm:order-2 flex items-center justify-center space-x-2"
              >
                {editingUser ? (
                  <>
                    <Edit className="w-4 h-4" />
                    <span>Update User</span>
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4" />
                    <span>Add User</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 sm:p-6 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600" />
    </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Delete User</h3>
                <p className="text-sm text-gray-600">This action cannot be undone</p>
              </div>
            </div>

            {/* Warning Message */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">!</span>
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800">
                    Are you sure you want to delete this user?
                  </p>
                  <p className="text-sm text-red-700 mt-1">
                    All data associated with <strong>{userToDelete.name}</strong> will be permanently removed.
                  </p>
                </div>
              </div>
            </div>

            {/* User Details */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h4 className="text-sm font-medium text-gray-900 mb-2">User Details:</h4>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <UserPlus className="w-4 h-4" />
                  <span><strong>Name:</strong> {userToDelete.name}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <span><strong>Email:</strong> {userToDelete.email}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Shield className="w-4 h-4" />
                  <span><strong>Username:</strong> {userToDelete.username}</span>
                </div>
                {userToDelete.roles && userToDelete.roles.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <Shield className="w-4 h-4" />
                    <span><strong>Roles:</strong> {userToDelete.roles.map(role => role.name).join(', ')}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end space-y-3 sm:space-y-0 sm:space-x-3">
              <button
                onClick={cancelDeleteUser}
                disabled={deleteLoading}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50 order-2 sm:order-1"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteUser}
                disabled={deleteLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-red-400 disabled:cursor-not-allowed flex items-center justify-center space-x-2 order-1 sm:order-2"
              >
                {deleteLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    <span>Delete User</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Profile Modal */}
      {showViewModal && viewingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 sm:p-6 w-full max-w-4xl max-h-[85vh] overflow-y-auto shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <UserAvatar user={viewingUser} size="xl" />
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{viewingUser.name}</h3>
                  <p className="text-gray-600">{viewingUser.email}</p>
                </div>
              </div>
              <button
                onClick={closeViewModal}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Profile Content */}
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="bg-gray-50 rounded-xl p-4 sm:p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <UserPlus className="w-5 h-5" />
                  <span>Basic Information</span>
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Full Name</label>
                    <div className="flex items-center space-x-2 p-3 bg-white rounded-lg border">
                      <UserPlus className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900">{viewingUser.name || 'Not provided'}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Username</label>
                    <div className="flex items-center space-x-2 p-3 bg-white rounded-lg border">
                      <Shield className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900">{viewingUser.username || 'Not provided'}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Email Address</label>
                    <div className="flex items-center space-x-2 p-3 bg-white rounded-lg border">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900">{viewingUser.email}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Phone Number</label>
                    <div className="flex items-center space-x-2 p-3 bg-white rounded-lg border">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900">{viewingUser.phone || 'Not provided'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Account Status */}
              <div className="bg-gray-50 rounded-xl p-4 sm:p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <Shield className="w-5 h-5" />
                  <span>Account Status</span>
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Email Verification</label>
                    <div className="flex items-center space-x-2">
                      {viewingUser.email_verified_at ? (
                        <div className="flex items-center space-x-2 p-3 bg-green-50 rounded-lg border border-green-200">
                          <Check className="w-4 h-4 text-green-600" />
                          <span className="text-green-800 font-medium">Verified</span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2 p-3 bg-red-50 rounded-lg border border-red-200">
                          <X className="w-4 h-4 text-red-600" />
                          <span className="text-red-800 font-medium">Not Verified</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Account Status</label>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        viewingUser.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {viewingUser.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Roles and Permissions */}
              {viewingUser.roles && viewingUser.roles.length > 0 && (
                <div className="bg-gray-50 rounded-xl p-4 sm:p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                    <Shield className="w-5 h-5" />
                    <span>Roles & Permissions</span>
                  </h4>
                  <div className="space-y-3">
                    {viewingUser.roles.map((role, index) => (
                      <div key={index} className="flex items-center space-x-3 p-3 bg-white rounded-lg border">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 text-sm font-bold">
                            {getRoleIcon(role.slug)}
                          </span>
                        </div>
                        <div className="flex-1">
                          <h5 className="font-medium text-gray-900">{role.name}</h5>
                          <p className="text-sm text-gray-600">{role.description || 'No description available'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Account Information */}
              <div className="bg-gray-50 rounded-xl p-4 sm:p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <Calendar className="w-5 h-5" />
                  <span>Account Information</span>
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Member Since</label>
                    <div className="flex items-center space-x-2 p-3 bg-white rounded-lg border">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900">
                        {formatDate(viewingUser.created_at)}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Last Updated</label>
                    <div className="flex items-center space-x-2 p-3 bg-white rounded-lg border">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900">
                        {formatDate(viewingUser.updated_at)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end space-y-3 sm:space-y-0 sm:space-x-3 mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={closeViewModal}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors order-2 sm:order-1"
              >
                Close
              </button>
              <PermissionGate permission="users.edit">
                <button
                  onClick={() => {
                    closeViewModal();
                    handleEditUser(viewingUser);
                  }}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 order-1 sm:order-2"
                >
                  <Edit className="w-4 h-4" />
                  <span>Edit User</span>
                </button>
              </PermissionGate>
            </div>
          </div>
        </div>
      )}

      {/* Verification Confirmation Modal */}
      {showVerifyModal && userToVerify && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 sm:p-6 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Verify User Email</h3>
                <p className="text-sm text-gray-600">Manually verify user's email address</p>
              </div>
            </div>

            {/* User Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h4 className="text-sm font-medium text-gray-900 mb-2">User Details:</h4>
              <div className="space-y-1 text-sm text-gray-600">
                <div><strong>Name:</strong> {userToVerify.name}</div>
                <div><strong>Email:</strong> {userToVerify.email}</div>
                <div><strong>Username:</strong> {userToVerify.username}</div>
              </div>
            </div>

            {/* Reason Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Manual Verification *
              </label>
              <textarea
                value={verifyReason}
                onChange={(e) => setVerifyReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="e.g., Email delivery issues, corporate user, account recovery..."
                rows={3}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Please provide a clear reason for manual verification. This will be logged for audit purposes.
              </p>
            </div>

            {/* Warning */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
              <div className="flex items-start space-x-2">
                <div className="flex-shrink-0">
                  <div className="w-4 h-4 bg-yellow-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">!</span>
                  </div>
                </div>
                <div className="text-sm text-yellow-800">
                  <p className="font-medium">Important:</p>
                  <p>Only verify users you have personally confirmed. This action will be logged and audited.</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end space-y-3 sm:space-y-0 sm:space-x-3">
              <button
                onClick={cancelVerifyUser}
                disabled={verifyLoading}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50 order-2 sm:order-1"
              >
                Cancel
              </button>
              <button
                onClick={confirmVerifyUser}
                disabled={verifyLoading || !verifyReason.trim()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-green-400 disabled:cursor-not-allowed flex items-center justify-center space-x-2 order-1 sm:order-2"
              >
                {verifyLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Verifying...</span>
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Verify User</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unverify Confirmation Modal */}
      {showUnverifyModal && userToUnverify && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 sm:p-6 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <X className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Remove Email Verification
                </h3>
                <p className="text-sm text-gray-600">
                  This will require the user to verify their email again
                </p>
              </div>
            </div>

            {/* User Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="space-y-2 text-sm">
                <div><strong>Name:</strong> {userToUnverify.name}</div>
                <div><strong>Email:</strong> {userToUnverify.email}</div>
                <div><strong>Username:</strong> {userToUnverify.username}</div>
                <div><strong>Current Status:</strong> 
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                    userToUnverify.status === 'inactive' 
                      ? 'bg-gray-100 text-gray-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {userToUnverify.status === 'inactive' ? 'Inactive' : 'Active'}
                  </span>
                  <span className="ml-2 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    ✓ Verified
                  </span>
                </div>
              </div>
            </div>

            {/* Reason Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for removing verification <span className="text-red-500">*</span>
              </label>
              <textarea
                value={unverifyReason}
                onChange={(e) => setUnverifyReason(e.target.value)}
                placeholder="Enter reason for removing email verification..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                rows="3"
              />
              <p className="text-xs text-gray-500 mt-1">
                This reason will be logged for audit purposes
              </p>
            </div>

            {/* Warning */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
              <div className="flex items-start space-x-2">
                <div className="flex-shrink-0">
                  <div className="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">!</span>
                  </div>
                </div>
                <div className="text-sm text-red-800">
                  <p className="font-medium">Warning:</p>
                  <p>This user will need to verify their email again before they can access their account.</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end space-y-3 sm:space-y-0 sm:space-x-3">
              <button
                onClick={cancelUnverifyUser}
                disabled={unverifyLoading}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50 order-2 sm:order-1"
              >
                Cancel
              </button>
              <button
                onClick={confirmUnverifyUser}
                disabled={unverifyLoading || !unverifyReason.trim()}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:bg-orange-400 disabled:cursor-not-allowed flex items-center justify-center space-x-2 order-1 sm:order-2"
              >
                {unverifyLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Removing...</span>
                  </>
                ) : (
                  <>
                    <X className="w-4 h-4" />
                    <span>Remove Verification</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* OTP Verification Modal */}
      {showOTPModal && userToVerifyOTP && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 sm:p-6 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Check className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Verify User with OTP
                </h3>
                <p className="text-sm text-gray-600">
                  Enter the OTP code sent to the user's email
                </p>
              </div>
            </div>

            {/* User Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="space-y-2 text-sm">
                <div><strong>Name:</strong> {userToVerifyOTP.name}</div>
                <div><strong>Email:</strong> {userToVerifyOTP.email}</div>
                <div><strong>Username:</strong> {userToVerifyOTP.username}</div>
                <div><strong>Current Status:</strong> 
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                    userToVerifyOTP.status === 'inactive' 
                      ? 'bg-gray-100 text-gray-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {userToVerifyOTP.status === 'inactive' ? 'Inactive' : 'Active'}
                  </span>
                  <span className="ml-2 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    ⚠ Unverified
                  </span>
                </div>
              </div>
            </div>

            {/* OTP Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                OTP Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={otpCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setOtpCode(value);
                  setOtpError('');
                }}
                placeholder="Enter 6-digit OTP code"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-lg font-mono tracking-widest"
                maxLength="6"
                disabled={otpLoading}
              />
              {otpError && (
                <p className="text-red-500 text-sm mt-1">{otpError}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Check the user's email for the 6-digit OTP code
              </p>
            </div>

            {/* Resend OTP Button */}
            <div className="mb-6">
              <button
                onClick={handleResendOTP}
                disabled={otpLoading}
                className="w-full px-4 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {otpLoading ? 'Sending...' : 'Resend OTP'}
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end space-y-3 sm:space-y-0 sm:space-x-3">
              <button
                onClick={cancelOTPVerification}
                disabled={otpLoading}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50 order-2 sm:order-1"
              >
                Cancel
              </button>
              <button
                onClick={handleVerifyOTP}
                disabled={otpLoading || !otpCode.trim() || otpCode.length !== 6}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center space-x-2 order-1 sm:order-2"
              >
                {otpLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Verifying...</span>
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Verify OTP</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Confirmation Modal */}
      {showStatusModal && userToStatusChange && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 sm:p-6 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center space-x-3 mb-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                newStatus === 'active' ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                <Power className={`w-6 h-6 ${
                  newStatus === 'active' ? 'text-green-600' : 'text-gray-600'
                }`} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {newStatus === 'active' ? 'Activate User' : 'Deactivate User'}
                </h3>
                <p className="text-sm text-gray-600">
                  {newStatus === 'active' 
                    ? 'Grant access to the system' 
                    : 'Remove access to the system'
                  }
                </p>
              </div>
            </div>

            {/* User Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h4 className="text-sm font-medium text-gray-900 mb-2">User Details:</h4>
              <div className="space-y-1 text-sm text-gray-600">
                <div><strong>Name:</strong> {userToStatusChange.name}</div>
                <div><strong>Email:</strong> {userToStatusChange.email}</div>
                <div><strong>Username:</strong> {userToStatusChange.username}</div>
                <div><strong>Current Status:</strong> 
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                    userToStatusChange.status === 'inactive' 
                      ? 'bg-gray-100 text-gray-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {userToStatusChange.status === 'inactive' ? 'Inactive' : 'Active'}
                  </span>
                </div>
              </div>
            </div>

            {/* Impact Warning */}
            <div className={`rounded-lg p-4 mb-6 ${
              newStatus === 'active' 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-start space-x-2">
                <div className="flex-shrink-0">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                    newStatus === 'active' ? 'bg-green-500' : 'bg-red-500'
                  }`}>
                    <span className="text-white text-xs font-bold">!</span>
                  </div>
                </div>
                <div className={`text-sm ${
                  newStatus === 'active' ? 'text-green-800' : 'text-red-800'
                }`}>
                  <p className="font-medium">
                    {newStatus === 'active' ? 'Activation Impact:' : 'Deactivation Impact:'}
                  </p>
                  <p className="mt-1">
                    {newStatus === 'active' 
                      ? 'User will regain full access to the system and can log in normally.'
                      : 'User will lose access to the system and cannot log in until reactivated.'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Reason Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Status Change *
              </label>
              <textarea
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={
                  newStatus === 'active' 
                    ? 'e.g., Employee onboarding, access restoration, policy compliance...'
                    : 'e.g., Security incident, policy violation, account compromise...'
                }
                rows={3}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Please provide a clear reason for this status change. This will be logged for audit purposes.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end space-y-3 sm:space-y-0 sm:space-x-3">
              <button
                onClick={cancelStatusChange}
                disabled={statusLoading}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50 order-2 sm:order-1"
              >
                Cancel
              </button>
              <button
                onClick={confirmStatusChange}
                disabled={statusLoading || !statusReason.trim()}
                className={`px-4 py-2 text-white rounded-lg transition-colors disabled:cursor-not-allowed flex items-center justify-center space-x-2 order-1 sm:order-2 ${
                  newStatus === 'active' 
                    ? 'bg-green-600 hover:bg-green-700 disabled:bg-green-400' 
                    : 'bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400'
                }`}
              >
                {statusLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>{newStatus === 'active' ? 'Activating...' : 'Deactivating...'}</span>
                  </>
                ) : (
                  <>
                    <Power className="w-4 h-4" />
                    <span>{newStatus === 'active' ? 'Activate User' : 'Deactivate User'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
};

export default UserManagement;
